import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, BRAND } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

function LangSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="language-switcher"
      className={`inline-flex items-center rounded-sm border border-slate-300 overflow-hidden text-xs font-semibold ${
        compact ? "" : ""
      }`}
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 uppercase tracking-wide transition-colors ${
            lang === l
              ? "bg-[#1B3A5B] text-white"
              : "bg-white text-slate-600 hover:text-[#1B3A5B]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const LINKS = [
    { to: "/", label: t.nav.home, testid: "nav-home-link", end: true },
    { to: "/a-propos", label: t.nav.about, testid: "nav-about-link" },
    { to: "/services", label: t.nav.services, testid: "nav-services-link" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-slate-200"
          : "bg-white border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            onClick={() => setOpen(false)}
          >
            <img
              src={BRAND.logo}
              alt="Logo DS EXPERT"
              className="h-11 w-11 object-contain"
            />
            <span className="font-serif-display text-xl font-bold tracking-tight text-[#1B3A5B] leading-none">
              DS EXPERT
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `nav-underline text-sm font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[#1B3A5B] active"
                      : "text-slate-600 hover:text-[#1B3A5B]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <LangSwitch />
            <a
              href={`tel:${CONTACT.phoneHref}`}
              data-testid="nav-phone-cta"
              className="inline-flex items-center gap-2 bg-[#C9A15A] text-white hover:bg-[#B38C48] transition-colors duration-300 px-5 py-2.5 text-sm font-medium rounded-sm"
            >
              <Phone className="h-4 w-4" />
              {t.nav.callUs}
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <LangSwitch compact />
            <button
              data-testid="mobile-menu-toggle"
              className="text-[#1B3A5B] p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-slate-200 bg-white px-6 py-4"
        >
          <nav className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-slate-100 ${
                    isActive ? "text-[#C9A15A]" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${CONTACT.phoneHref}`}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#C9A15A] text-white px-5 py-3 text-sm font-medium rounded-sm"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
